(() => {
  const PAGE_SIZE = 12;
  const CATEGORY_LABELS = {
    all: 'すべての記事',
    game: 'ゲーム別PCガイド',
    parts: 'PC選び・パーツ解説',
    notice: 'お知らせ'
  };

  const normalize = (value) =>
    String(value || '')
      .normalize('NFKC')
      .toLocaleLowerCase('ja-JP')
      .replace(/\s+/g, ' ')
      .trim();

  const initArchive = (root) => {
    if (root.dataset.newsArchiveReady === 'true') return;
    root.dataset.newsArchiveReady = 'true';

    const grid = root.querySelector('[data-news-grid]');
    const searchForm = root.querySelector('[data-news-search-form]');
    const searchInput = root.querySelector('[data-news-search-input]');
    const clearButton = root.querySelector('[data-news-clear]');
    const status = root.querySelector('[data-news-status]');
    const serverPagination = root.querySelector('[data-news-server-pagination]');
    const filterPagination = root.querySelector('[data-news-filter-pagination]');
    const filterPage = root.querySelector('[data-news-filter-page]');
    const previousButton = root.querySelector('[data-news-prev]');
    const nextButton = root.querySelector('[data-news-next]');
    const categoryButtons = [...root.querySelectorAll('button[data-news-category]')];

    if (!grid || !searchForm || !searchInput || !status) return;

    const initialMarkup = grid.innerHTML;
    const pageCount = Number(root.dataset.newsPages || 1);
    let activeCategory = 'all';
    let activeQuery = '';
    let resultPage = 1;
    let indexedCards = null;
    let indexPromise = null;
    let currentResults = [];

    const cardFromNode = (node) => ({
      key: node.dataset.newsKey,
      category: node.dataset.newsCategory || 'parts',
      search: normalize(node.dataset.newsSearch),
      html: node.outerHTML
    });

    const setActiveCategory = (category) => {
      activeCategory = CATEGORY_LABELS[category] ? category : 'all';
      categoryButtons.forEach((button) => {
        const active = button.dataset.newsCategory === activeCategory;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const setStatus = (message, state = 'result') => {
      status.hidden = false;
      status.dataset.state = state;
      status.textContent = message;
    };

    const updateUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');

      if (activeCategory === 'all') {
        url.searchParams.delete('tg_category');
      } else {
        url.searchParams.set('tg_category', activeCategory);
      }

      if (activeQuery) {
        url.searchParams.set('q', searchInput.value.trim());
      } else {
        url.searchParams.delete('q');
      }

      window.history.replaceState({}, '', url);
    };

    const loadAllCards = async () => {
      if (indexedCards) return indexedCards;
      if (indexPromise) return indexPromise;

      indexPromise = (async () => {
        root.setAttribute('aria-busy', 'true');
        setStatus('全記事を読み込んでいます。', 'loading');

        const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);
        const pages = await Promise.all(
          pageNumbers.map(async (pageNumber) => {
            const url = new URL(window.location.href);
            url.searchParams.set('view', 'news-v2');
            url.searchParams.set('page', String(pageNumber));
            url.searchParams.delete('tg_category');
            url.searchParams.delete('q');

            const response = await fetch(url, {
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'TITAN-News-Archive' }
            });
            if (!response.ok) throw new Error(`Failed to load page ${pageNumber}`);

            const documentText = await response.text();
            return new DOMParser().parseFromString(documentText, 'text/html');
          })
        );

        const cards = [];
        const seen = new Set();
        pages.forEach((documentNode) => {
          documentNode.querySelectorAll('[data-news-card]').forEach((node) => {
            const card = cardFromNode(node);
            if (!card.key || seen.has(card.key)) return;
            seen.add(card.key);
            cards.push(card);
          });
        });

        indexedCards = cards;
        root.removeAttribute('aria-busy');
        return indexedCards;
      })().catch((error) => {
        indexPromise = null;
        root.removeAttribute('aria-busy');
        throw error;
      });

      return indexPromise;
    };

    const renderResults = () => {
      const totalPages = Math.max(1, Math.ceil(currentResults.length / PAGE_SIZE));
      resultPage = Math.min(Math.max(resultPage, 1), totalPages);
      const offset = (resultPage - 1) * PAGE_SIZE;
      const visibleCards = currentResults.slice(offset, offset + PAGE_SIZE);

      if (visibleCards.length) {
        grid.innerHTML = visibleCards.map((card) => card.html).join('');
      } else {
        grid.innerHTML = '<p class="tg-news__empty">該当する記事はありません。別のキーワードやカテゴリーをお試しください。</p>';
      }

      if (serverPagination) serverPagination.hidden = true;
      if (filterPagination) {
        filterPagination.hidden = currentResults.length <= PAGE_SIZE;
        filterPage.textContent = `${resultPage} / ${totalPages}`;
        previousButton.disabled = resultPage <= 1;
        nextButton.disabled = resultPage >= totalPages;
      }

      const label = CATEGORY_LABELS[activeCategory];
      const queryLabel = activeQuery ? `「${searchInput.value.trim()}」` : '';
      setStatus(`${label}${queryLabel ? `・${queryLabel}` : ''}：${currentResults.length}件`);
      clearButton.hidden = false;
    };

    const applyFilter = async ({ resetPage = true } = {}) => {
      activeQuery = normalize(searchInput.value);
      if (activeCategory === 'all' && !activeQuery) {
        grid.innerHTML = initialMarkup;
        if (serverPagination) serverPagination.hidden = false;
        if (filterPagination) filterPagination.hidden = true;
        status.hidden = true;
        status.removeAttribute('data-state');
        clearButton.hidden = true;
        updateUrl();
        return;
      }

      try {
        const cards = await loadAllCards();
        const queryParts = activeQuery.split(' ').filter(Boolean);
        currentResults = cards.filter((card) => {
          const categoryMatches = activeCategory === 'all' || card.category === activeCategory;
          const queryMatches = queryParts.every((part) => card.search.includes(part));
          return categoryMatches && queryMatches;
        });
        if (resetPage) resultPage = 1;
        renderResults();
        updateUrl();
      } catch (error) {
        setStatus('記事一覧の読み込みに失敗しました。時間をおいて再度お試しください。', 'error');
      }
    };

    const scrollToTools = () => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      root.querySelector('.tg-news__tools')?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    };

    categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setActiveCategory(button.dataset.newsCategory);
        applyFilter();
      });
    });

    searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      applyFilter();
    });

    clearButton.addEventListener('click', () => {
      searchInput.value = '';
      setActiveCategory('all');
      activeQuery = '';
      applyFilter();
      searchInput.focus();
    });

    previousButton?.addEventListener('click', () => {
      if (resultPage <= 1) return;
      resultPage -= 1;
      renderResults();
      scrollToTools();
    });

    nextButton?.addEventListener('click', () => {
      const totalPages = Math.ceil(currentResults.length / PAGE_SIZE);
      if (resultPage >= totalPages) return;
      resultPage += 1;
      renderResults();
      scrollToTools();
    });

    const params = new URL(window.location.href).searchParams;
    const requestedCategory = params.get('tg_category');
    const requestedQuery = params.get('q');
    if (requestedCategory || requestedQuery) {
      setActiveCategory(requestedCategory || 'all');
      searchInput.value = requestedQuery || '';
      applyFilter();
    }
  };

  document.querySelectorAll('[data-news-archive]').forEach(initArchive);
})();
